import { AxiosResponse } from "axios";
import {
  SET_PHONE_NUMBERS,
  SET_PHONE_NUMBER,
  SET_ERRORMSG,
  SET_PHONE_NUMBER_ROTATIONS,
} from "@/store/mutation-types";
import {
  CLEAR_PHONE_NUMBER_ISLOADING,
  DELETE_PHONE_NUMBER,
  GET_PHONE_NUMBER_ROTATIONS,
  GET_PHONE_NUMBERS,
  REGISTER_PHONE_NUMBER,
} from "@/store/action-types";
import { StateObject } from "@/dtos/State.dto";
import PhoneNumber from "@/services/phone-number";
import { Module } from "vuex";
import { RegisterPhoneNumber, GetPhoneNumber } from "@/dtos/PhoneNumber.dto";

const phoneNumber: Module<any, {}> = {
  state: {
    phoneNumber: {
      data: null,
      isLoading: false,
    },
    phoneNumbers: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    phoneNumberRotations: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    phoneNumber: (state: { phoneNumber: any }) => state.phoneNumber,
    phoneNumbers: (state: { phoneNumbers: any }) => state.phoneNumbers,
    phoneNumberRotations: (state: { phoneNumberRotations: any }) => state.phoneNumberRotations,
  },
  mutations: {
    [SET_PHONE_NUMBER]: (
      state: { phoneNumber: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.phoneNumber.data = payload.data;
      state.phoneNumber.isLoading = payload.isLoading;
    },
    [SET_PHONE_NUMBERS]: (
      state: { phoneNumbers: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.phoneNumbers.data = payload.data;
      if (payload.metaData != null) state.phoneNumbers.metaData = payload.metaData;
      state.phoneNumbers.isLoading = payload.isLoading;
    },
    [SET_PHONE_NUMBER_ROTATIONS]: (
      state: { phoneNumberRotations: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.phoneNumberRotations.data = payload.data;
      if (payload.metaData != null) state.phoneNumberRotations.metaData = payload.metaData;
      state.phoneNumberRotations.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_PHONE_NUMBER]: async ({ commit }: any, payload: any) => {
      commit(SET_PHONE_NUMBER, { data: null, isLoading: true });

      const result: AxiosResponse = await PhoneNumber.registerPhoneNumber(
        payload as RegisterPhoneNumber
      );

      if (result) {
        if (result.data.status) {
          commit(SET_PHONE_NUMBER, {
            data: result.data.data,
            isLoading: false,
          });
          return true;
        }
        commit(SET_PHONE_NUMBER, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_PHONE_NUMBER, { isLoading: false });
        return false;
      }
    },
    [GET_PHONE_NUMBERS]: async ({ commit }: any, payload: any) => {
      commit(SET_PHONE_NUMBERS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await PhoneNumber.getPhoneNumbers(payload as GetPhoneNumber);

        if (result) {
          if (result.data.status) {
            commit(SET_PHONE_NUMBERS, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_PHONE_NUMBERS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_PHONE_NUMBERS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_PHONE_NUMBERS, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(
            SET_ERRORMSG,
            "Request timed out. The server is taking too long to respond. Please try again later."
          );
        } else if (error.response?.status === 500) {
          commit(
            SET_ERRORMSG,
            "Server error occurred while loading phone numbers. Please try again later."
          );
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Phone numbers not found. Please contact support.");
        } else if (!error.response) {
          commit(
            SET_ERRORMSG,
            "Network error. Please check your internet connection and try again."
          );
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load phone numbers. Please try again."
          );
        }

        return false;
      }
    },
    [GET_PHONE_NUMBER_ROTATIONS]: async ({ commit }: any, payload: any) => {
      commit(SET_PHONE_NUMBER_ROTATIONS, { data: null, isLoading: true });

      const result: AxiosResponse = await PhoneNumber.getRotatedPhoneNumbers(
        payload as GetPhoneNumber
      );

      if (result) {
        if (result.data.status) {
          commit(SET_PHONE_NUMBER_ROTATIONS, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_PHONE_NUMBER_ROTATIONS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_PHONE_NUMBER_ROTATIONS, { isLoading: false });
        return false;
      }
    },
    [DELETE_PHONE_NUMBER]: async ({ commit }: any, payload: string) => {
      commit(SET_PHONE_NUMBER, { data: { id: payload }, isLoading: true });

      const result: AxiosResponse = await PhoneNumber.deletePhoneNumber(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_PHONE_NUMBER, {
            data: result.data.data,
            isLoading: false,
          });
          return true;
        }
        commit(SET_PHONE_NUMBER, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_PHONE_NUMBER, { isLoading: false });
        return false;
      }
    },
    [CLEAR_PHONE_NUMBER_ISLOADING]: ({ commit }) => {
      commit(SET_PHONE_NUMBER, { isLoading: false });
      commit(SET_PHONE_NUMBERS, { isLoading: false });
      commit(SET_PHONE_NUMBER_ROTATIONS, { isLoading: false });
    },
  },
};

export default phoneNumber;
